import clsx from "clsx";
import {
    isExperiencePurchaseItem,
    isMembershipItem,
    isOtherSellerPurchase,
    isPurchaseItemCanceled,
    Loader,
} from "common";
import React, { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Event, Experience, Membership, Permission, Purchase, PurchaseItem, PurchaseItemObject } from "types";
import { NotesListPerType } from "../Dashboard/Roster/NotesListPerType";
import { ErrorBoundary } from "../ErrorBoundary";
import { CancellationProtectionSummary } from "./CancellationProtectionSummary";
import { PurchaseCustomerInformation } from "./CustomerInformation/PurchaseCustomerInformation";
import { PaymentSummary } from "./PaymentSummary/PaymentSummary";
import { PurchaseItemActionsBar } from "./PurchaseItemActionsBar";
import { PurchaseItemBanner } from "./PurchaseItemBanner";
import { PurchaseTimeline } from "./Timeline/PurchaseTimeline";
import { MembershipPurchaseTerms } from "@/components/PurchasesMenu/MembershipPurchaseTerms";
import { useProduct } from "@/hooks/resources/useProduct";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useQueryParams } from "@/hooks/useQueryParams";
import { useResource } from "@/hooks/useResource";
import { useVisibility } from "@/hooks/useVisibility";

const CreatePurchaseSplitPaymentModal = React.lazy(async () =>
    import("../PurchaseCreate/SplitPayment/CreatePurchaseSplitPaymentModal").then((module) => ({
        default: module.CreatePurchaseSplitPaymentModal,
    })),
);
const CheckInAndPrintTicket = React.lazy(async () =>
    import("../Settings/App/PurchaseCreate/CheckInAndPrintTicket").then((module) => ({
        default: module.CheckInAndPrintTicket,
    })),
);

interface PurchaseItemDetailsProps {
    purchase: Purchase;
    item?: PurchaseItem;
    isDrawer?: boolean;
    onContactInfoClick?: () => void;
}

export const PurchaseItemDetails = ({
    purchase,
    item,
    isDrawer = false,
    onContactInfoClick,
}: PurchaseItemDetailsProps) => {
    const history = useHistory();
    const seller = useSeller();
    const itemDetailsRef = useRef<HTMLDivElement | null>(null);
    const { isGranted } = useAuthorization();
    const [purchaseForPrintModal, setPurchaseForPrintModal] = useState<Purchase>();
    const [
        isCheckInAndPrintTicketModalOpen,
        { show: showCheckInAndPrintTicketModal, hide: hideCheckInAndPrintTicketModal },
    ] = useVisibility(false);

    const queryParams = useQueryParams();
    const isSplitPayOpen = queryParams.get("showSplitPay") === "true";

    const defaultItem = item ?? purchase.items[0];
    const isExperience = isExperiencePurchaseItem(defaultItem);
    const isMembership = isMembershipItem(item);
    const isPartnerPurchaseWithoutEditPermissions =
        isOtherSellerPurchase(purchase, seller.id) && !purchase.partner?.editPurchases;
    const isUnScheduled = defaultItem.object === PurchaseItemObject.ORDER;
    const isTimeslotsAvailable = isExperience && !isUnScheduled;
    const isMistItem = item && purchase.items.length > 1;
    const isItemCanceled = isPurchaseItemCanceled(defaultItem);

    const { data: event } = useResource<Event>(["events", defaultItem.event?.id], null, {
        enabled:
            !!defaultItem.event?.id &&
            isGranted(Permission.MODIFY_BOOKING_LITE) &&
            !isOtherSellerPurchase(purchase, seller.id),
    });

    const organizerId = purchase.organizer?.id;
    const productId = defaultItem.product?.id;

    const fetchParams = { enabled: isTimeslotsAvailable };
    const { product, isLoadingProduct } = useProduct<Experience | Membership>(productId, null, fetchParams);

    const isStoreCredit = defaultItem.object === PurchaseItemObject.STORE_CREDIT;
    const shouldShowNotes =
        organizerId &&
        !isStoreCredit &&
        !isMistItem &&
        isGranted(Permission.PURCHASE_NOTES_VIEW) &&
        !isOtherSellerPurchase(purchase, seller.id);

    const handleOnCloseSplitPaymentModal = useCallback(() => {
        history.replace(`/purchases/${purchase.id}`);

        if (seller.preferences.ticket?.autoPrint) {
            setPurchaseForPrintModal(purchase);
            showCheckInAndPrintTicketModal();
        }
    }, [history, purchase, seller.preferences.ticket?.autoPrint, showCheckInAndPrintTicketModal]);

    useEffect(() => {
        if (itemDetailsRef.current) {
            itemDetailsRef.current.scroll(0, 0);
        }
    }, [item?.id]);

    return (
        <div
            ref={itemDetailsRef}
            id={isDrawer ? "purchase-drawer" : ""}
            className={clsx("flex w-full flex-col overflow-y-auto", isDrawer ? "p-1" : " p-1 px-4 py-10 xl:px-10")}
        >
            <ErrorBoundary fallbackComponent={<PurchaseItemBannerFallback purchase={purchase} item={item} />}>
                <PurchaseItemBanner purchase={purchase} item={item} event={isItemCanceled ? undefined : event} />
            </ErrorBoundary>
            <PurchaseItemActionsBar
                key={purchase.id}
                purchase={purchase}
                item={item}
                event={isItemCanceled ? undefined : event}
                experience={product as Experience}
                isLoading={isLoadingProduct}
                className={clsx(
                    "flex h-14 w-full items-center px-1",
                    isMembership || isPartnerPurchaseWithoutEditPermissions ? "justify-start" : "justify-between",
                )}
            />
            <div className="mt-14 grid grid-cols-2 space-x-2 xl:space-x-10">
                <PurchaseCustomerInformation
                    purchase={purchase}
                    item={item}
                    product={product}
                    onContactInfoClick={onContactInfoClick}
                />
                <div className="space-y-4">
                    <PaymentSummary purchase={purchase} item={item} />
                    <CancellationProtectionSummary purchase={purchase} item={item} />
                </div>
            </div>
            {isMembership && item && product && (
                <>
                    <hr className="my-10 text-gray-light" />
                    <MembershipPurchaseTerms membership={product as Membership} item={item} />
                </>
            )}
            <hr className="mt-10 text-gray-light" />
            <div className="w-full divide-y divide-gray-lighter">
                {shouldShowNotes && (
                    <NotesListPerType
                        showCurrentPurchaseNotes
                        event={event}
                        purchase={purchase}
                        userId={organizerId}
                        className="py-10"
                        item={item}
                    />
                )}
                {isPartnerPurchaseWithoutEditPermissions ? null : <PurchaseTimeline purchase={purchase} item={item} />}
            </div>

            {/* Suspense is used to lazy load the modals, added in https://xola01.atlassian.net/browse/X2-8948 to improve performance
            Please contact @rushi or @khomyakov if you have any questions or going to change this */}

            <Suspense fallback={<Loader />}>
                <CreatePurchaseSplitPaymentModal
                    isOpen={isSplitPayOpen}
                    purchase={purchase}
                    travelerId={purchase.travelers[0]?.id}
                    onClose={handleOnCloseSplitPaymentModal}
                />
                {purchaseForPrintModal && (
                    <CheckInAndPrintTicket
                        isOpen={isCheckInAndPrintTicketModalOpen}
                        purchase={purchaseForPrintModal}
                        onComplete={() => {
                            hideCheckInAndPrintTicketModal();
                            history.replace(`/purchases/${purchase.id}`);
                        }}
                    />
                )}
            </Suspense>
        </div>
    );
};

const PurchaseItemBannerFallback = ({ purchase, item }: { purchase: Purchase; item?: PurchaseItem }) => {
    let displayText = "Xola ran into an error while loading this purchase";
    try {
        displayText += ` for ${purchase.customerName}`;
    } catch {}

    return (
        <div className="ui-product-banner flex h-40 min-h-[160px] items-center justify-between rounded-lg bg-gray-dark px-8 text-white">
            <h3>{item?.name ?? "Error"}</h3>
            <div>{displayText}</div>
        </div>
    );
};
